import moment from "moment"
import PropTypes from "prop-types"
import { validate } from "uuid"
import { ERROR, LOADING, UNKNOWN, OK } from "helpers/data-statuses"
import { ASC, DESC } from "helpers/sorts"

function chainablePropTypes(predicate) {
  const propType = (props, propName, componentName) => {
    // don't do any validation if empty
    if (props[propName]) {
      return predicate(props, propName, componentName)
    }
  }

  propType.isRequired = (props, propName, componentName) => {
    // warn if empty
    if (props[propName] == null) {
      return new Error(`Required prop \`${propName}\` was not specified in \`${componentName}\`.`)
    }

    return predicate(props, propName, componentName)
  }

  return propType
}

export function isValidDate(date) {
  return moment(date, "YYYY-MM-DD").isValid()
}

export const DATE = chainablePropTypes((props, propName, componentName) => {
  if (!isValidDate(props[propName])) {
    return new Error(`Invalid date '${propName}' supplied to '${componentName}'`)
  }
})

export const UUID = chainablePropTypes((props, propName, componentName) => {
  if (!validate(props[propName])) {
    return new Error(`Invalid UUID '${propName}' supplied to '${componentName}'`)
  }
})

export const PERIOD = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    startDate: DATE.isRequired,
    endDate: DATE.isRequired,
  }),
])

export const CHART_ITEM = PropTypes.shape({
  date: DATE.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
})

export const OPTIONAL_CHART_ITEM = PropTypes.shape({
  date: DATE.isRequired,
  value: PropTypes.number,
  index: PropTypes.number.isRequired,
})

export const FACTOR_CATEGORY = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
})

export const USER = PropTypes.shape({
  id: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isSuperuser: PropTypes.bool,
  idHash: PropTypes.string.isRequired,
})

export const CONTENT_SET = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
})

export const DROPDOWN_OPTION = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  count: PropTypes.number,
})

export const OPERATOR = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  queryFormatter: PropTypes.func.isRequired,
  validator: PropTypes.func.isRequired,
})

export const FILTER_ON = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  dataType: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  operators: PropTypes.arrayOf(OPERATOR),
  isDisabled: PropTypes.bool,
  options: PropTypes.arrayOf(DROPDOWN_OPTION),
})

export const FILTER_ON_GROUP = PropTypes.shape({
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(FILTER_ON).isRequired,
})

export const FILTER = PropTypes.shape({
  filterOn: FILTER_ON,
  operator: OPERATOR,
  values: PropTypes.array,
  isExisting: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
})

export const CHIP = PropTypes.shape({
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
})

export const SEGMENT = PropTypes.shape({
  filters: PropTypes.arrayOf(PropTypes.object),
  normalization: PropTypes.oneOf(["CONTRIBUTION", "STANDALONE"]),
})

export const SEARCHABLE_MENU_ITEM = PropTypes.shape({
  order: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  menuItemTooltip: PropTypes.node,
  groupId: PropTypes.string,
  groupLabel: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    component: PropTypes.node,
    onClick: PropTypes.func,
  })),
  newFeature: PropTypes.string,
  secondaryAction: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    onClick: PropTypes.func.isRequired,
    tooltip: PropTypes.node,
  }),
  tertiaryAction: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    onClick: PropTypes.func.isRequired,
    tooltip: PropTypes.node,
  }),
})

export const ACCOUNT_MEMBER = PropTypes.shape({
  id: UUID.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  createdAt: DATE.isRequired,
  updatedAt: DATE.isRequired,
  isActivated: PropTypes.bool.isRequired,
})

export const PERMISSIONED_TOKEN = PropTypes.shape({
  id: UUID.isRequired,
  name: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  createdBy: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  createdAt: DATE.isRequired,
  lastUsedAt: DATE,
  expiresOn: DATE,
})

export const PERMISSION_LEVEL = PropTypes.oneOf(["READ", "WRITE", "OWNER"])

export const RESOURCE_TYPE = PropTypes.oneOf(["PORTFOLIO", "WATCHLIST", "CLASSIFICATION", "RESEARCH_TOPIC", "FORECAST", "CONTENT_SET", "SWAP"])

export const RESOURCE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  type: RESOURCE_TYPE.isRequired,
  alias: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  updatedAt: DATE,
  permissionLevel: PERMISSION_LEVEL.isRequired,
})

export const PERMISSION = PropTypes.shape({
  id: PropTypes.string.isRequired,
  type: RESOURCE_TYPE.isRequired,
  alias: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  permissionLevel: PERMISSION_LEVEL.isRequired,
  createdAt: DATE.isRequired,
  updatedAt: DATE.isRequired,
})

export const FIELD = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["string", "text", "email", "select", "multi", "alias", "date"]).isRequired,
  validations: PropTypes.shape({
    isRequired: PropTypes.bool,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    minDate: PropTypes.string,
  }),
  options: PropTypes.array,
  optionLabels: PropTypes.object,
})

export const ROLE = PropTypes.oneOf(["member", "admin"])

/**
 * track: 'analytics_track_type' // payload will be the submission data
 *
 * or
 *
 * track: {
 *   type: 'analytics_track_type',
 *   [ payload: object payload or func that receives submission data and returns payload ]
 * }
 */
export const ANALYTICS_TRACKING = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    type: PropTypes.string.isRequired,
    payload: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.func,
    ]),
  }),
])

export const TABLE_GROUP_DROPDOWN = PropTypes.shape({
  items: PropTypes.array,
  columns: PropTypes.array,
  selectedId: PropTypes.string,
  backAction: PropTypes.object,
})

export const DATASET = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
})

export const DATASET_TEAM = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  alias: PropTypes.string.isRequired,
})

export const DATASET_MEMBER = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
})

export const DATASET_TOKEN = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
})

export const REPORT_ENTITY = PropTypes.shape({
  member: DATASET_MEMBER,
  token: DATASET_TOKEN,
  direct: PropTypes.bool.isRequired,
  teams: PropTypes.arrayOf(DATASET_TEAM).isRequired,
})

export const LIST_ITEM_TEXT_OPTION = PropTypes.shape({
  id: PropTypes.string.isRequired,
  primaryText: PropTypes.node.isRequired,
  secondaryText: PropTypes.node,
})

export const OPTIONS_TOGGLE_ITEM = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
})

export const OPTIONS_PAGE_SELECTOR = PropTypes.shape({
  value: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  tooltip: PropTypes.string,
})

export const PERIOD_SELECTOR_LABELS = PropTypes.shape({
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
})

export const TOKEN = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  lastUsedAt: DATE,
  createdAt: DATE.isRequired,
})

export const CURRENCY = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
})

export const RESOURCE_LIMITS = PropTypes.shape({
  current: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  available: PropTypes.number.isRequired,
})

export const SORT_DIRECTION = PropTypes.oneOf([ASC, DESC])

export const SMART_TRADE_RULE = PropTypes.shape({
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  unit: PropTypes.string,
  info: PropTypes.string,
  category: PropTypes.string,
  error: PropTypes.string,
  isCurrency: PropTypes.bool,
  getter: PropTypes.func,
  maxOnly: PropTypes.bool,
})

export const MODEL = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
})

export const SECURITY = PropTypes.shape({
  ticker: PropTypes.string,
  assetClass: PropTypes.string.isRequired,
  assetSubclass: PropTypes.string,
  internalId: PropTypes.string,
})

export const UNIVERSAL_SEARCH_RESULT = PropTypes.shape({
  id: PropTypes.string.isRequired,
  assetClass: PropTypes.string.isRequired,
  assetSubclass: PropTypes.string,
  country: PropTypes.string,
  currency: PropTypes.string,
  exchange: PropTypes.string,
  internalId: PropTypes.string,
  modelProviderId: PropTypes.string,
  name: PropTypes.string.isRequired,
  score: PropTypes.number,
  sector: PropTypes.string,
  ticker: PropTypes.string,
  type: PropTypes.string.isRequired,
})

export const ROLLUP = PropTypes.shape({
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isClassification: PropTypes.bool,
    proprietay: PropTypes.bool,
  })).isRequired,
})

export const SORT_ORDER = PropTypes.shape({
  direction: SORT_DIRECTION.isRequired,
  id: PropTypes.string.isRequired,
  absolute: PropTypes.bool,
})

export const DRIFT_VALUES = PropTypes.shape({
  mean: PropTypes.number.isRequired,
  stdDev: PropTypes.number.isRequired,
  change: PropTypes.number,
})

export const RESOURCE_STATUS = PropTypes.oneOf([LOADING, ERROR, UNKNOWN, OK])
